import _ from "lodash";
import { useEffect, useState, useMemo } from "react";
import { Card, Grid, Typography } from "@mui/material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Legend,
  Brush,
  Line,
} from "recharts";

import TimeUtils from "utils/time";
import AnalyticsService from "data/firestore/analytics";
import { MethodCategories, MethodColors } from "data/methods";

const renderTooltipContent = (o) => {
  const { payload, label } = o;
  const total = payload.reduce((result, entry) => result + entry.value, 0);

  const breakdown = TimeUtils.breakdown(total, true);
  const displayString = TimeUtils.elapsedToString(breakdown);

  const tooltipHeader = `${label} (${displayString})`;
  if (total) {
    AnalyticsService.throttleChartHover(
      "hover_on_methods_over_time",
      {},
      tooltipHeader
    );
  }

  return (
    <Card
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        borderRadius: 24,
        color: "white",
        padding: 15,
      }}
    >
      <Typography gutterBottom>{tooltipHeader}</Typography>
      <Grid container flexDirection="column" textAlign="right">
        {payload.map((entry, index) => (
          <Typography
            key={`item-${index}`}
            style={{ color: entry.color, lineHeight: 1.5 }}
            variant="overline"
          >
            {`${entry.name}: ${_.round((entry.value / total) * 100)}%`}
          </Typography>
        ))}
      </Grid>
    </Card>
  );
};

function createLegendOpacityMap(opacity) {
  return _.mapValues(_.keyBy(_.values(MethodCategories)), () => opacity);
}

const StackedAreaChart = ({
  data,
  brushRange,
  setBrushRange,
  timelineData,
  metric,
}) => {
  // Filter data based on brush range if provided
  const filteredData = useMemo(() => {
    if (brushRange && brushRange.length === 2) {
      const [startIndex, endIndex] = brushRange;
      return data.slice(startIndex, endIndex + 1);
    }
    return data;
  }, [data, brushRange]);

  // Filter down to first month of the year only
  const ticks = useMemo(() => {
    return _.filter(filteredData, (d) => _.includes(d.x, "Jan")).map(
      (d) => d.x
    );
  }, [filteredData]);

  const [opacity, setOpacity] = useState(createLegendOpacityMap(1));
  const [categoriesWithData, setCategoriesWithData] = useState(new Set());

  useEffect(() => {
    const list = new Set();
    _.forEach(filteredData, (d) => _.forEach(_.keys(d), (k) => list.add(k)));
    list.delete("x");
    setCategoriesWithData(list);
  }, [filteredData]);

  function handleMouseEnter(o) {
    const { dataKey } = o;

    const legend = createLegendOpacityMap(0.25);
    legend[dataKey] = 1;
    setOpacity(legend);
  }

  function handleMouseLeave(o) {
    const legend = createLegendOpacityMap(1);
    setOpacity(legend);
  }

  function handleBrushChange(brushData) {
    if (
      brushData &&
      brushData.startIndex !== undefined &&
      brushData.endIndex !== undefined
    ) {
      setBrushRange([brushData.startIndex, brushData.endIndex]);
    } else {
      setBrushRange(null);
    }
  }

  return (
    <>
      <ResponsiveContainer width="100%" height="90%">
        <ComposedChart
          width={500}
          height={400}
          data={filteredData}
          stackOffset="expand"
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            ticks={ticks}
            tickFormatter={(t) => _.slice(t, -4).join("")}
            padding={{ left: 20, right: 10 }}
          />
          <YAxis tickFormatter={(n) => `${n * 100}%`} />
          <Tooltip content={renderTooltipContent} />
          {_.map(MethodColors, (color, category) => {
            if (!categoriesWithData.has(category)) {
              return null;
            }

            return (
              <Bar
                key={category}
                dataKey={category}
                stroke={color[500]}
                opacity={_.get(opacity, category)}
                fill={color[500]}
                type="monotone"
                stackId="1"
              />
            );
          })}
          <Legend
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          {/* Invisible line chart for tooltip syncing */}
          {timelineData && (
            <Line
              dataKey={metric}
              stroke="transparent"
              strokeWidth={0}
              fill="transparent"
              opacity={0}
              activeDot={false}
              connectNulls={false}
              name="invisible"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      <ResponsiveContainer width="100%" height="10%">
        <ComposedChart
          width={500}
          height={30}
          data={data}
          margin={{
            top: 0,
            right: 30,
            left: 55,
            bottom: 0,
          }}
        >
          <Brush
            stroke="#b0bec5"
            height={30}
            dataKey="x"
            onChange={handleBrushChange}
            startIndex={brushRange ? brushRange[0] : 0}
            endIndex={brushRange ? brushRange[1] : data.length - 1}
            travellerWidth={10}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};

export default StackedAreaChart;
